import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, X } from "lucide-react";
import { API, useAuth } from "@/App";
import { normalizeRole } from "@/lib/permissions";
import ScheduleMeetingModal from "@/components/ScheduleMeetingModal";
import { DISPO } from "./ClientCallDispositionModal";

// EHI client AI products are always offered as extra "Products to Demo" chips.
const CLIENT_DEMO_PRODUCTS = DISPO.products;

// Browser offset like "-04:00" so the backend stores an unambiguous ISO time.
const tzOffset = () => {
  const mins = -new Date().getTimezoneOffset();
  const abs = Math.abs(mins);
  return `${mins >= 0 ? "+" : "-"}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
};

// "+HH:MM" offset of an IANA zone at a given instant.
const zoneOffset = (tz, instant) => {
  try {
    const v = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" }).formatToParts(instant).find((p) => p.type === "timeZoneName")?.value || "";
    const m = /GMT([+-]\d{2}):?(\d{2})?/.exec(v);
    return m ? `${m[1]}:${m[2] || "00"}` : "+00:00";
  } catch { return null; }
};

// Convert the picked wall-clock time in `tz` to ET (how bookings are listed) with its ET offset.
const toEastern = (date, time, tz) => {
  if (!date || !time) return { date, time, offset: tzOffset() };
  const guess = new Date(`${date}T${time}:00Z`);
  const off = zoneOffset(tz || "America/New_York", guess) || tzOffset();
  const instant = new Date(`${date}T${time}:00${off}`);
  const et = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(instant);
  const g = (t) => et.find((p) => p.type === t)?.value;
  return { date: `${g("year")}-${g("month")}-${g("day")}`, time: `${g("hour")}:${g("minute")}`, offset: zoneOffset("America/New_York", instant) || off };
};

function ClientPicker({ onPick, onClose }) {
  const { token } = useAuth();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRows([]); setBusy(false); return undefined; }
    setBusy(true);
    const h = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/ehi-clients`, { params: { q: term, limit: 20 }, headers: { Authorization: `Bearer ${token}` } });
        setRows(r.data?.items || []);
      } catch { setRows([]); }
      setBusy(false);
    }, 300);
    return () => clearTimeout(h);
  }, [q, token]);
  const hint = (t) => <div style={{ padding: 24, textAlign: "center", color: "#94A3B8", fontSize: 11 }}>{t}</div>;
  return (
    <div data-testid="client-demo-picker" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{ position: "fixed", inset: 0, zIndex: 110, background: "rgba(11,17,32,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: 500, maxWidth: "100%", background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 60px -16px rgba(0,0,0,0.25)" }}>
        <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #E5E7EB", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>Book a client demo</div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>Start by picking an EHI client.</div>
          </div>
          <button data-testid="client-demo-picker-close" onClick={onClose} aria-label="Close" style={{ background: "transparent", border: 0, cursor: "pointer", color: "#64748B", padding: 4 }}><X style={{ width: 16, height: 16 }} /></button>
        </div>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, minHeight: 200 }}>
          <div style={{ position: "relative" }}>
            <Search style={{ position: "absolute", left: 10, top: 10, width: 13, height: 13, color: "#94A3B8" }} />
            <input data-testid="client-demo-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by practice name, state or email…"
              style={{ width: "100%", padding: "9px 10px 9px 30px", fontSize: 12, borderRadius: 8, border: "0.5px solid #CBD5E1", background: "#F8FAFC", boxSizing: "border-box", fontFamily: "inherit" }} />
          </div>
          <div style={{ overflowY: "auto", maxHeight: 320, border: "0.5px solid #E2E8F0", borderRadius: 8 }}>
            {busy && <div style={{ padding: 20, textAlign: "center", color: "#64748B", fontSize: 12 }}><Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 6 }} />Searching…</div>}
            {!busy && q.trim().length < 2 && hint("Type at least 2 characters to search EHI clients.")}
            {!busy && q.trim().length >= 2 && rows.length === 0 && hint("No matching clients.")}
            {!busy && rows.map((c) => (
              <button key={c.client_id} type="button" data-testid={`client-demo-option-${c.client_id}`} onClick={() => onPick(c)}
                style={{ width: "100%", textAlign: "left", background: "transparent", border: 0, borderBottom: "0.5px solid #F1F5F9", padding: "9px 12px", cursor: "pointer", fontFamily: "inherit" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#0F172A" }}>{c.clinic || `Client ${c.client_id}`}</div>
                <div style={{ fontSize: 11, color: "#64748B" }}>#{c.client_id}{c.state_code ? ` · ${c.state_code}` : ""}{c.email ? ` · ${c.email}` : ""}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Shared client demo booking pop-up (EHI client page, Client Bookings, future EHI dialer).
 * `client` = { client_id, clinic, phone, email } prefills; `contact` = { name, email, phone } optional.
 */
export default function ClientDemoBookingModal({ client: initialClient, contact, products: presetProducts, date: presetDate, time: presetTime, onClose, onBooked }) {
  const { token, user } = useAuth();
  const [client, setClient] = useState(initialClient || null);
  const readOnly = normalizeRole(user?.role) === "read_only";
  useEffect(() => {
    if (readOnly) { toast.error("Your role can view client demos but not book them", { id: "client-demo-ro" }); onClose?.(); }
  }, [readOnly]); // eslint-disable-line react-hooks/exhaustive-deps -- run once on open
  if (readOnly) return null;
  if (!client) return <ClientPicker onPick={setClient} onClose={onClose} />;
  const clinic = client.clinic || `Client ${client.client_id}`;
  const email = contact?.email || client.email || null;
  const save = async (m) => {
    const toEmail = email || m.client_email;
    const attendees = [...new Set([toEmail, ...(m.cc_emails || [])].filter((e) => e && e.includes("@")))];
    const when = toEastern(m.date, m.time, m.tz);
    const withName = /[a-z]/i.test(m.meetingWith || "") ? m.meetingWith : (contact?.name || "");
    try {
      const r = await axios.post(`${API}/demo-bookings/schedule`, {
        lead_id: `ehi-${client.client_id}`,
        date: when.date || "", time: when.time || "", tz_offset: when.offset, duration: 45,
        demo_type: String(m.type || "product_demo").toLowerCase().trim().replace(/\s+/g, "_"),
        product: (Array.isArray(m.products) && m.products.length ? m.products : (presetProducts || [])).join(", "),
        host_rep_id: m.host_user_id || null, host_rep_name: m.host_name || "",
        attendees, contact_name: withName, contact_email: toEmail || "",
        meeting_link: m.link || "", internal_notes: m.notes || "", notes: m.title || "",
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 });
      onBooked?.({ id: r.data?.id || r.data?.demo?.id || r.data?.booking?.id || null, date: m.date, time: m.time, tz: m.tz, products: m.products || [] });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(`Couldn't book the client demo${typeof d === "string" ? `: ${d}` : ""}`);
    }
  };
  return (
    <ScheduleMeetingModal
      lead={{
        id: `ehi-${client.client_id}`, clinic, phone: contact?.phone || client.phone || null, email,
        contacts: contact?.name ? [{ name: contact.name, email: contact.email, phone: contact.phone }] : [],
        contact_name: contact?.name || null, timezone: null, require_client_email: true,
        products_pitched: presetProducts || [], extra_products: CLIENT_DEMO_PRODUCTS, preset_date: presetDate || null, preset_time: presetTime || null,
      }}
      onClose={onClose}
      onSave={save}
    />
  );
}
